/**
 * Hand-level presentation for 500: what a card is marked with, the order it is
 * fanned in, and how a card is named in a sentence on the felt.
 *
 * None of it re-derives a rule. `rankingFor` hands the wire payloads to the
 * *shared engine's* `fhRanking`, so "is this a trump", "what suit does it count
 * as" and "how strong is it" have one implementation in the repo and cannot
 * disagree with the server. The client's own copy — `games/five-hundred/
 * ranking.ts` — called a nominated Joker a trump in no-trumps and misère, so a
 * player watched the Joker take a trick the server then awarded elsewhere; it
 * is deleted rather than patched (VERDICT H1). Who is taking the trick now
 * comes from `Trick.winning`, which the reducer publishes.
 *
 * Sort order runs low on the left to high on the right, with the trump block
 * last: plain suits first, each ascending by rank, then the trumps ascending
 * (…, left bower, right bower, Joker) hard against the right edge of the fan.
 *
 * That is the hand a right-hander physically arranges, and it is also where
 * Trickster's `HandSortStyle.highOnRight` puts it. It matters more here than
 * on a table: the fan overlaps left to right, so the *right-most* card is the
 * only one showing its whole face, and the cards nearest the thumb are the
 * ones you reach for. The trump block — the thing you count — ends up under
 * the thumb rather than across the hand from it.
 *
 * The plain suits alternate colour, re-derived rather than fixed, because
 * pulling the trump suit out of a fixed ♠♥♣♦ order can leave two blacks
 * adjacent — which on the two-colour deck (now the default) is two suits that
 * look identical at a fan's worth of overlap.
 */
import {
  DEFAULT_FH_CONFIG,
  fhRanking,
  isLeftBower,
  isRightBower,
  type CardId,
  type FhConfig,
  type FhContract,
  type FhHandExtra,
  type FhLevel,
  type FhStrain,
  type RankingContext,
  type Seat,
  type Suit,
} from '@cards/shared'
import type { CardMarks } from '../types'
import type { TableView } from '../../table/view'
import { readContract, readJokerSuit, specLabel } from './spec'

const SUIT_ORDER: readonly Suit[] = ['S', 'H', 'C', 'D']
const RED: ReadonlySet<Suit> = new Set<Suit>(['H', 'D'])

function asLevel(n: number): FhLevel | null {
  return n === 6 || n === 7 || n === 8 || n === 9 || n === 10 ? n : null
}

/** The contract as the 500 module's own type. `hand.contract` really is an
 *  `FhContract` on the wire — `games/types.ts` says a GameUi may read its own
 *  game's opaque payloads — but it is re-read through `spec.ts` because that
 *  reader also copes with the label-only fallback the server sends with calls.
 *  `rank` is the ladder position and exists only to compare bids, which the
 *  client never does, so it is left at 0. */
function contractFor(view: TableView): FhContract | null {
  const read = readContract(view.hand?.contract)
  if (!read) return null
  const contractor = (read.contractor ?? 0) as Seat
  if (read.spec.kind === 'suit') {
    const level = asLevel(read.spec.level)
    if (level === null) return null
    const strain = read.spec.strain as FhStrain
    return {
      spec: { kind: 'suit', level, strain },
      rank: 0,
      value: read.value,
      tricks: level,
      mode: strain === 'NT' ? 'nt' : 'suit',
      trumpSuit: strain === 'NT' ? null : strain,
      contractor,
      label: specLabel(read.spec),
    }
  }
  return {
    spec: { kind: read.spec.kind },
    rank: 0,
    value: read.value,
    tricks: 0,
    mode: read.spec.kind,
    trumpSuit: null,
    contractor,
    label: specLabel(read.spec),
  }
}

/** `hand.extra` is the module's own `FhHandExtra`; only `jokerNominated` bears
 *  on ranking, but the whole shape is filled so the engine is handed the type
 *  it declares rather than a cast. `{}` before the first deal. */
function extraFor(view: TableView): FhHandExtra {
  const raw = (view.hand?.extra ?? {}) as Partial<FhHandExtra>
  return {
    jokerNominated: readJokerSuit(view.hand?.extra),
    jokerNominationResolved: raw.jokerNominationResolved ?? false,
    suitsLed: raw.suitsLed ?? [],
    leadNominations: raw.leadNominations ?? [],
    revealedVoids: raw.revealedVoids ?? [],
    kittyTaken: raw.kittyTaken ?? false,
    discardResolved: raw.discardResolved ?? false,
    openMisereExposed: raw.openMisereExposed ?? false,
    sittingOut: raw.sittingOut ?? null,
  }
}

function configFor(view: TableView): FhConfig {
  const raw = (view.match?.config ?? view.snapshot.room.config ?? {}) as Partial<FhConfig>
  return { ...DEFAULT_FH_CONFIG, ...raw }
}

// One context per hand object: `cardMarks` is asked once per card in the fan
// and `sortHand` once per render, and the snapshot gives a fresh hand object
// only when something actually changed.
const contexts = new WeakMap<object, RankingContext>()

export function rankingFor(view: TableView): RankingContext {
  const key = view.hand as object | null
  if (key) {
    const hit = contexts.get(key)
    if (hit) return hit
  }
  const ctx = fhRanking(contractFor(view), extraFor(view), configFor(view))
  if (key) contexts.set(key, ctx)
  return ctx
}

/** The cards that came from the kitty, as far as this viewer may know. The
 *  pile itself is never shown mid-hand, and the core empties it the moment
 *  the contractor picks it up, so the contractor learns them from their own
 *  discard prompt, whose payload reaches nobody else. After the hand the
 *  pile is public; before or without a prompt, nothing is marked. */
export function kittyCardsFor(view: TableView): readonly CardId[] {
  const hand = view.hand
  if (!hand) return []
  if (hand.kitty.kind === 'cards' && hand.kitty.cards.length > 0) return hand.kitty.cards
  const pending = hand.pending
  if (pending?.kind !== 'discardKitty' || pending.seat !== view.mySeat) return []
  const payload = pending.payload as { kitty?: unknown } | null
  return Array.isArray(payload?.kitty) ? (payload.kitty as readonly CardId[]) : []
}

export function cardMarksFor(
  card: CardId,
  ctx: RankingContext,
  kitty: readonly CardId[],
): CardMarks {
  const trump = ctx.trumpSuit
  return {
    trump: ctx.isTrump(card),
    stamp:
      trump === null || card === 'JOKER'
        ? null
        : isRightBower(card, trump)
          ? 'Right'
          : isLeftBower(card, trump)
            ? 'Left'
            : null,
    fromKitty: kitty.includes(card),
  }
}

/** The plain suits in the order they are fanned, colours alternating where the
 *  pack allows. Greedy over `SUIT_ORDER`: take the first suit left, then the
 *  first whose colour differs from the one just placed. With hearts trump that
 *  turns ♠♣♦ into ♠♦♣ rather than seating the two black suits side by side. */
function plainSuitOrder(exclude: Suit | null): Suit[] {
  const left = SUIT_ORDER.filter((suit) => suit !== exclude)
  const out: Suit[] = []
  while (left.length > 0) {
    const last = out[out.length - 1]
    const at = last ? left.findIndex((suit) => RED.has(suit) !== RED.has(last)) : 0
    out.push(...left.splice(at === -1 ? 0 : at, 1))
  }
  return out
}

export function sortHand(cards: readonly CardId[], ctx: RankingContext): CardId[] {
  const order = plainSuitOrder(ctx.trumpSuit)
  // Trumps take the last group, so they land at the right-hand end; a card
  // whose suit cannot be read at all (a floating Joker in no-trumps is a trump
  // and never reaches here) sits just inside them.
  const group = (card: CardId): number => {
    if (ctx.isTrump(card)) return order.length + 1
    const suit = ctx.effectiveSuit(card)
    return suit ? order.indexOf(suit) : order.length
  }
  return [...cards].sort((a, b) => {
    const ga = group(a)
    const gb = group(b)
    if (ga !== gb) return ga - gb
    return ctx.strength(a) - ctx.strength(b)
  })
}

/** How the design names a card in a sentence on the felt. Empty when the card
 *  has no name of its own and should be spelled out as `10♠`. */
export function cardPhrase(card: CardId, ctx: RankingContext): string {
  const trump = ctx.trumpSuit
  if (card === 'JOKER') return 'the Joker'
  if (trump === null) return ''
  if (isRightBower(card, trump)) return 'the right bower'
  if (isLeftBower(card, trump)) return 'the left bower'
  return ''
}
